export const rasterMimeTypes = ["image/png", "image/jpeg", "image/webp", "image/gif"] as const;

export type RasterMimeType = (typeof rasterMimeTypes)[number];

const extensions: Readonly<Record<RasterMimeType, string>> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
};

export interface RasterImageFormat {
  readonly mimeType: RasterMimeType;
  readonly extension: string;
}

/** Validates an imported raster's signature, declared type, and static-GIF boundary. */
export function inspectRasterImage(
  bytes: Uint8Array<ArrayBuffer>,
  declaredMimeType: string,
): RasterImageFormat {
  const detectedMimeType = detectRasterMimeType(bytes);
  if (detectedMimeType === null) {
    throw new Error("The selected file is not a supported PNG, JPEG, WebP, or GIF image.");
  }
  if (declaredMimeType !== "" && declaredMimeType !== detectedMimeType) {
    throw new Error("The image contents do not match the file's declared media type.");
  }
  if (detectedMimeType === "image/gif" && countGifFrames(bytes) !== 1) {
    throw new Error("Shotboard accepts static GIF images; animated GIFs are not supported.");
  }
  return { mimeType: detectedMimeType, extension: extensions[detectedMimeType] };
}

/** Detects the supported raster type from its normative byte signature. */
function detectRasterMimeType(bytes: Uint8Array<ArrayBuffer>): RasterMimeType | null {
  if (hasBytes(bytes, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return "image/png";
  }
  if (hasBytes(bytes, 0, [0xff, 0xd8, 0xff])) {
    return "image/jpeg";
  }
  if (hasAscii(bytes, 0, "RIFF") && hasAscii(bytes, 8, "WEBP")) {
    return "image/webp";
  }
  if (hasAscii(bytes, 0, "GIF87a") || hasAscii(bytes, 0, "GIF89a")) {
    return "image/gif";
  }
  return null;
}

/** Counts complete GIF image descriptors while validating block boundaries. */
function countGifFrames(bytes: Uint8Array<ArrayBuffer>): number {
  if (bytes.byteLength < 14) {
    throw new Error("The GIF file is incomplete.");
  }

  const globalTableFlags = readByte(bytes, 10);
  let offset = 13;
  if ((globalTableFlags & 0x80) !== 0) {
    offset += 3 * 2 ** ((globalTableFlags & 0x07) + 1);
  }

  let frames = 0;
  let hasTrailer = false;
  while (offset < bytes.byteLength) {
    const marker = readByte(bytes, offset);
    offset += 1;
    if (marker === 0x3b) {
      hasTrailer = true;
      break;
    }
    if (marker === 0x21) {
      // Skip the extension label, then its size-prefixed data blocks.
      readByte(bytes, offset);
      offset = skipGifSubBlocks(bytes, offset + 1);
      continue;
    }
    if (marker !== 0x2c) {
      throw new Error("The GIF file contains an invalid block marker.");
    }

    frames += 1;
    const localTableFlags = readByte(bytes, offset + 8);
    offset += 9;
    if ((localTableFlags & 0x80) !== 0) {
      offset += 3 * 2 ** ((localTableFlags & 0x07) + 1);
    }
    // Skip the LZW minimum code size, then image data blocks.
    readByte(bytes, offset);
    offset = skipGifSubBlocks(bytes, offset + 1);
  }

  if (frames === 0) {
    throw new Error("The GIF file does not contain an image frame.");
  }
  if (!hasTrailer) {
    throw new Error("The GIF file is missing its trailer.");
  }
  return frames;
}

/** Advances over a terminated sequence of GIF size-prefixed blocks. */
function skipGifSubBlocks(bytes: Uint8Array<ArrayBuffer>, start: number): number {
  let offset = start;
  while (true) {
    const length = readByte(bytes, offset);
    offset += 1;
    if (length === 0) {
      return offset;
    }
    offset += length;
    if (offset > bytes.byteLength) {
      throw new Error("The GIF file contains a truncated data block.");
    }
  }
}

/** Reads one required byte with a stable invalid-file failure. */
function readByte(bytes: Uint8Array<ArrayBuffer>, offset: number): number {
  const value = bytes[offset];
  if (value === undefined) {
    throw new Error("The image file is incomplete.");
  }
  return value;
}

/** Compares a byte sequence without allocating a slice. */
function hasBytes(
  bytes: Uint8Array<ArrayBuffer>,
  offset: number,
  expected: readonly number[],
): boolean {
  return expected.every((value, index) => bytes[offset + index] === value);
}

/** Compares an ASCII signature without decoding untrusted file contents. */
function hasAscii(bytes: Uint8Array<ArrayBuffer>, offset: number, expected: string): boolean {
  return [...expected].every((value, index) => bytes[offset + index] === value.charCodeAt(0));
}
